import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  Lightbulb,
  FileText,
  Sparkles,
  PenTool,
  Bookmark,
  ScrollText,
  Film,
  Clapperboard,
  NotebookPen,
  Heart,
  Star,
  MessageSquareText,
  Megaphone,
  Pencil,
  Wand2,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const RING_ICONS = [
  // Outer ring (8 icons)
  [BookOpen, Film, Lightbulb, Megaphone, ScrollText, Clapperboard, Star, PenTool],
  // Middle ring (6 icons)
  [FileText, Sparkles, Bookmark, NotebookPen, MessageSquareText, Pencil],
  // Inner ring (4 icons)
  [Wand2, Heart, Lightbulb, Film],
];

const RING_COLORS = [
  "hsl(var(--primary))",
  "hsl(220 70% 55%)",
  "hsl(260 60% 55%)",
  "hsl(340 65% 55%)",
  "hsl(180 50% 45%)",
  "hsl(30 70% 50%)",
  "hsl(var(--primary) / 0.8)",
  "hsl(150 60% 45%)",
];

const FavoritesEmptyHero = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsPaused(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") setIsPaused(true);
      else { observer.unobserve(el); observer.observe(el); }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  const renderRing = (icons: React.ElementType[], radius: number, duration: number, reverse: boolean, ringIndex: number) => {
    const angleStep = 360 / icons.length;
    return (
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          width: radius * 2,
          height: radius * 2,
          animation: isPaused ? "none" : `${reverse ? "spin-slow-reverse" : "spin-slow"} ${duration}s linear infinite`,
        }}
      >
        {icons.map((IconComp, i) => {
          const angle = (angleStep * i * Math.PI) / 180;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const colorIdx = (ringIndex * 3 + i) % RING_COLORS.length;
          const iconSize = ringIndex === 0 ? 22 : ringIndex === 1 ? 20 : 18;

          return (
            <div
              key={i}
              className="absolute"
              style={{
                left: "50%",
                top: "50%",
                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
              }}
            >
              <div
                className="rounded-xl p-2.5 bg-card border border-border/60 shadow-lg backdrop-blur-sm"
                style={{
                  animation: isPaused ? "none" : `${reverse ? "spin-slow" : "spin-slow-reverse"} ${duration}s linear infinite`,
                }}
              >
                <IconComp
                  style={{ width: iconSize, height: iconSize, color: RING_COLORS[colorIdx] }}
                  strokeWidth={1.8}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full min-h-[520px] md:min-h-[600px] rounded-2xl overflow-hidden border border-border/50"
      style={{ background: "hsl(var(--background))" }}
    >
      <style>{`
        @keyframes spin-slow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes spin-slow-reverse {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(-360deg); }
        }
      `}</style>

      {/* Spinning rings of icons */}
      <div className="absolute inset-0 flex items-center justify-center">
        {renderRing(RING_ICONS[0], 290, 80, false, 0)}
        {renderRing(RING_ICONS[1], 210, 60, true, 1)}
        {renderRing(RING_ICONS[2], 140, 45, false, 2)}
      </div>

      {/* Radial gradient overlay - stronger to clear center */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,hsl(var(--background))_25%,transparent_55%,hsl(var(--background))_75%)]" />

      {/* Central content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6">
        <div className="text-center space-y-5">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
            <Heart className="h-7 w-7 text-primary" />
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              {t("favorites.noFavorites")}
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-md mx-auto">
              Odwiedź bibliotekę i wybierz ulubiony scenariusz
            </p>
          </div>

          <Button onClick={() => navigate("/biblioteka")} size="lg" className="shadow-lg">
            <BookOpen className="mr-2 h-5 w-5" />
            {t("favorites.browseContent")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FavoritesEmptyHero;
